import { useCallback, useState, type ReactNode } from "react";
import { api } from "./api";

// ═══════════════════════════════════════════════════════════════════════════
//  SESSION REVOCATION
// ═══════════════════════════════════════════════════════════════════════════
// The one destructive thing this console does, so it lives in one place and
// behaves the same everywhere it appears.
//
// ── The confirm is not boilerplate ─────────────────────────────────────────
// This signs a real person out of their phone, mid-task, with no warning to
// them and no way for them back in except a fresh OTP. It is the right answer
// to "someone has stolen this session" and the wrong answer to a misclick on a
// crowded table, and the two are one row apart. So the confirm names the
// customer and says what will happen, rather than asking "Are you sure?".

interface Revoker {
  revokeUser: (
    userId: string,
    name: string,
    reason?: string,
  ) => Promise<void>;
  revokeSession: (sessionId: string, label: string) => Promise<void>;
  /** The id currently in flight, for disabling the row's button. */
  busyId: string | null;
  toast: ReactNode;
}

export function useRevoke(onDone?: () => void): Revoker {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    text: string;
    error: boolean;
  } | null>(null);

  const flash = useCallback((text: string, error = false) => {
    setMessage({ text, error });
    window.setTimeout(() => setMessage(null), 5000);
  }, []);

  const revokeUser = useCallback(
    async (userId: string, name: string, reason?: string) => {
      const ok = window.confirm(
        `Sign ${name} out of every device?\n\n` +
          `Every phone and browser currently signed in to this account stops ` +
          `working on its next request. They will need a new OTP to get back ` +
          `in, and they are not notified.\n\n` +
          `This is recorded in the audit log against your account.`,
      );
      if (!ok) return;

      setBusyId(userId);
      try {
        const res = await api<{ sessionsEnded: number }>(
          `/api/admin/users/${userId}/revoke-sessions`,
          { method: "POST", body: { reason: reason ?? "admin console" } },
        );
        flash(
          `Ended ${res.sessionsEnded} session${res.sessionsEnded === 1 ? "" : "s"} for ${name}.`,
        );
        onDone?.();
      } catch (err) {
        flash(
          err instanceof Error ? err.message : "Could not end those sessions.",
          true,
        );
      } finally {
        setBusyId(null);
      }
    },
    [flash, onDone],
  );

  const revokeSession = useCallback(
    async (sessionId: string, label: string) => {
      const ok = window.confirm(
        `End this session?\n\n${label}\n\n` +
          `Only this one device is signed out. Recorded in the audit log.`,
      );
      if (!ok) return;

      setBusyId(sessionId);
      try {
        await api(`/api/admin/sessions/${sessionId}`, { method: "DELETE" });
        flash("Session ended.");
        onDone?.();
      } catch (err) {
        flash(
          err instanceof Error ? err.message : "Could not end that session.",
          true,
        );
      } finally {
        setBusyId(null);
      }
    },
    [flash, onDone],
  );

  const toast = message ? (
    <div
      className={message.error ? "toast toast-error" : "toast"}
      role="status"
      aria-live="polite"
    >
      {message.text}
    </div>
  ) : null;

  return { revokeUser, revokeSession, busyId, toast };
}
